import React, { useEffect, useState } from "react";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { frFR } from "@mui/x-data-grid/locales";
import { Button } from "@mui/material";

import FastAPIClient from "../../client";
import config from "../../config";

const client = new FastAPIClient(config);

interface Lead {
    client_id: string;
    client_name: string;
    warehouse_name: string | null;
    first_name: string | null;
    last_name: string | null;
    total_volume: number; // Assuming total_volume is a number
    created_at: string | null;
    last_meeting_date: string | null;
    next_meeting_date: string | null;
    city: string | null; // Add city field
    // Removed fields that are not returned by the backend:
    // id: number;
    // name: string;
    // mail: string | null;
    // phone: string | null;
    // website: string | null;
    // price_point: string | null;
    // cuisine: string | null;
    // address: string | null;
    // zip: string | null;
    // lat: number | null;
    // long: number | null;
    // relevance: string | null;
    // status: string | null;
}

// Helper to format dates in French locale
const formatDate = (value: string | null) => {
    if (!value) return "";
    const d = new Date(value);
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const LeadTable = () => {
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLeadTableData();
    }, []);

    const fetchLeadTableData = async () => {
        try {
            const response = await client.fetchLeadTable();
            console.log("leads", response.results);
            setLeads(response.results);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching leads:", error);
            setLoading(false);
        }
    };

    const columns: GridColDef[] = [
        { field: "warehouse_name", headerName: "Entrepôt", width: 150 },
        {
            field: "sales_rep",
            headerName: "Responsable",
            width: 200,
            renderCell: (params) => {
                // eslint-disable-line @typescript-eslint/no-explicit-any
                if (!params || !params.row) return "";
                return `${params.row.first_name || ""} ${
                    params.row.last_name || ""
                }`;
            },
        },
        { field: "client_name", headerName: "Nom du client", width: 250 },
        { field: "city", headerName: "Ville", width: 150 },
        { field: "total_volume", headerName: "Volume total", width: 150 },

        {
            field: "created_at",
            headerName: "Date de création",
            width: 150,
            renderCell: (params) => {
                if (
                    !params ||
                    params.value === undefined ||
                    params.value === null
                )
                    return null;
                return formatDate(params.value as string);
            },
        },
        {
            field: "last_meeting_date",
            headerName: "Dernière réunion",
            width: 150,
            renderCell: (params) => {
                if (
                    !params ||
                    params.value === undefined ||
                    params.value === null
                )
                    return null;
                return formatDate(params.value as string);
            },
        },
        {
            field: "next_meeting_date",
            headerName: "Prochaine réunion",
            width: 150,
            renderCell: (params) => {
                if (
                    !params ||
                    params.value === undefined ||
                    params.value === null
                )
                    return null;
                return formatDate(params.value as string);
            },
        },
        {
            field: "clientLink",
            headerName: "",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
                params.row.client_id && (
                    <Button
                        href={`/lead/${params.row.client_id}`}
                        variant="outlined"
                        size="small"
                        sx={{
                            backgroundColor: "#ffffff",
                            color: "#000000",
                            borderColor: "#000000",
                            borderRadius: "20px",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "#f5f5f5",
                                borderColor: "#000000",
                            },
                            minWidth: "auto",
                            padding: "2px 12px",
                        }}
                    >
                        Fiche prospect
                    </Button>
                ),
        },
        // Removed columns that are not available from the backend:
        // { field: 'name', headerName: 'Name', width: 200 },
        // { field: 'mail', headerName: 'Email', width: 200 },
        // { field: 'phone', headerName: 'Phone', width: 150 },
        // { field: 'address', headerName: 'Address', width: 250 },
        // { field: 'status', headerName: 'Status', width: 100 },
        // { field: 'relevance', headerName: 'Relevance', width: 150 },
    ];

    return (
        <div style={{ height: 400, width: "100%" }} className="mt-4">
            <DataGridPremium
                rows={leads}
                columns={columns}
                initialState={{
                    pagination: { paginationModel: { pageSize: 5, page: 0 } },
                    sorting: {
                        sortModel: [
                            { field: "next_meeting_date", sort: "desc" },
                        ],
                    },
                }}
                pageSizeOptions={[5, 10, 25]}
                loading={loading}
                getRowId={(row) => row.client_id}
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
            />
        </div>
    );
};

export default LeadTable;
